import React from "react";
import { useSearchParams } from "react-router-dom";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { faGift } from "@fortawesome/free-solid-svg-icons";
import styles from "./Promociones.module.css";
import PromoCard from "./PromoCard";
import usePromotions from "./usePromotions";
import useDocumentMeta from "../blog/useDocumentMeta";
import { EmptyState, Spinner, Tabs } from "../../../components/ui";
import { Reveal, Stagger, StaggerItem } from "../../../components/motion";
import { DEFAULT_PROMO_FILTER, PROMO_FILTERS } from "./promoUtils";

const EMPTY_BY_FILTER = {
  active: "No hay promociones activas en este momento",
  upcoming: "Aún no hay promociones próximas",
  inactive: "No hay promociones finalizadas",
  all: "Aún no hay promociones",
};

/** Estado amable cuando el módulo de promociones está apagado en el backend. */
export function PromotionsComingSoon() {
  return (
    <div className={styles.comingSoon}>
      <span className={styles.comingSoonIcon} aria-hidden="true">
        <FontAwesomeIcon icon={faGift} />
      </span>
      <h2 className={styles.comingSoonTitle}>
        Muy pronto tendremos promociones exclusivas para ti
      </h2>
      <p className={styles.comingSoonText}>
        Estamos preparando rifas y beneficios para los usuarios de Gold Loterías.
        Vuelve pronto.
      </p>
    </div>
  );
}

const PromocionesPage = () => {
  const [searchParams, setSearchParams] = useSearchParams();
  const requested = searchParams.get("estado");
  const filter = PROMO_FILTERS.some((f) => f.id === requested) ? requested : DEFAULT_PROMO_FILTER;
  const { promotions, loading, failed, featureDisabled, reload } = usePromotions({
    status: filter,
  });

  useDocumentMeta(
    "Promociones | Gold Loterías",
    "Rifas y promociones exclusivas para los usuarios de Gold Loterías."
  );

  const changeFilter = (id) => {
    const next = new URLSearchParams(searchParams);
    if (id === DEFAULT_PROMO_FILTER) next.delete("estado");
    else next.set("estado", id);
    setSearchParams(next, { replace: true });
  };

  return (
    <div className={styles.page}>
      <Reveal as="header" className={styles.listHeader}>
        <p className={styles.eyebrow}>Promociones</p>
        <h1 className={styles.listTitle}>
          Promociones exclusivas para usuarios de Gold Loterías
        </h1>
        <p className={styles.listSubtitle}>
          Participa en rifas con tu saldo y aprovecha los beneficios que tenemos para ti.
        </p>
      </Reveal>

      <div className={styles.filtersRow}>
        <Tabs
          tabs={PROMO_FILTERS}
          activeId={filter}
          onChange={changeFilter}
          className={styles.filters}
        />
      </div>

      {loading && <Spinner label="Cargando promociones…" />}

      {/* Con el módulo de rifas apagado (`feature_disabled`) el backend igual devuelve las
          promociones informativas (PROMO_INFO): se muestran; "Muy pronto" solo si no hay nada. */}
      {!loading && promotions.length === 0 && featureDisabled && !failed && (
        <PromotionsComingSoon />
      )}

      {!loading && promotions.length === 0 && (!featureDisabled || failed) && (
        <EmptyState
          title={failed ? "No pudimos cargar las promociones" : EMPTY_BY_FILTER[filter]}
          description={
            failed
              ? "Revisa tu conexión e inténtalo de nuevo."
              : "Vuelve pronto: publicamos nuevas promociones con frecuencia."
          }
          actionLabel={failed ? "Reintentar" : undefined}
          onAction={failed ? reload : undefined}
        />
      )}

      {!loading && promotions.length > 0 && (
        <Stagger key={filter} className={styles.grid} stagger={0.06}>
          {promotions.map((promo) => (
            <StaggerItem key={promo.slug} className="motion-fill">
              <PromoCard promo={promo} />
            </StaggerItem>
          ))}
        </Stagger>
      )}
    </div>
  );
};

export default PromocionesPage;
